import React, { useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

export const CheckLiveMail = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [inputListMail, setInputListMail] = useState('');
  const [listLive, setListLive] = useState([]);
  const [listDie, setListDie] = useState([]);
  const [listUnknown, setListUnknown] = useState([]);
  const [totalItemCheck, setTotalItemCheck] = useState(0);
  const [itemChecked, setItemChecked] = useState(0);
  const progress = useMemo(
    () => (totalItemCheck === 0 ? 0 : +((itemChecked / totalItemCheck) * 100).toFixed(1)),
    [itemChecked, totalItemCheck],
  );

  const onClickCheckMail = async () => {
    setTotalItemCheck(0);
    setItemChecked(0);
    setLoading(true);
    setListUnknown([]);
    setListLive([]);
    setListDie([]);
    const dataValid = inputListMail.split('\n').filter(x => !!x.trim());
    setTotalItemCheck(dataValid.length);
    try {
      const { data: dataRes } = await axios.post('/api/tools/check-live-mail', { mail: dataValid });
      const eventSource = new EventSource(`/api/tools/check-live-mail/stream?sessionId=${dataRes.data}`);
      eventSource.onmessage = event => {
        const data = JSON.parse(event.data);
        if (data.status === 'live') setListLive(prev => [...prev, data.mailpass]);
        else if (data.status === 'die') setListDie(prev => [...prev, data.mailpass]);
        else setListUnknown(prev => [...prev, data.mailpass]);
        setItemChecked(prev => prev + 1);
      };
      eventSource.onerror = () => {
        eventSource.close();
        setLoading(false);
        Swal.fire({ title: t('toolCommon.complete'), icon: 'success' });
      };
    } catch (e) {
      /* error */ setLoading(false);
      Swal.fire({ title: t('toolCommon.errorOccurred'), icon: 'error' });
    }
  };

  const onCopy = data => {
    navigator.clipboard.writeText(data);
    toast.success(t('toolCommon.copySuccess'));
  };
  const cats = [
    { key: 'live', label: 'Live', data: listLive, color: colors.success, icon: '✅' },
    { key: 'die', label: 'Die', data: listDie, color: colors.danger, icon: '❌' },
    { key: 'unknown', label: 'Unknown', data: listUnknown, color: colors.info, icon: '❓' },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: 24 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        ✉️ Check Live Mail
      </h2>
      <div className="ms-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.muted }}>{t('toolCheckMail.mailList')}</span>
          <span className="ms-badge ms-badge--dim">
            {inputListMail.split('\n').filter(x => !!x).length} {t('toolCommon.lines')}
          </span>
        </div>
        <textarea
          className="ms-textarea"
          style={{ height: 160 }}
          value={inputListMail}
          placeholder="mail|pass|refresh_token|client_id"
          onChange={e => setInputListMail(e.target.value)}
        />
        <button
          className={`ms-btn ${loading ? 'ms-btn--danger' : 'ms-btn--primary'}`}
          onClick={onClickCheckMail}
          style={{ width: '100%', marginTop: 10 }}
        >
          {loading ? t('toolCommon.checking') : t('toolCommon.startCheck')}
        </button>
        {(loading || progress > 0) && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: colors.dim }}>{t('toolCommon.progress')}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {progress}%
              </span>
            </div>
            <div className="ms-progress">
              <div className="ms-progress__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {cats.map(c => (
          <div key={c.key} className="ms-card" style={{ padding: 14, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: c.color }}>
                {c.icon} {c.label}
              </span>
              <span className="ms-badge" style={{ color: c.color, background: `${c.color}22`, borderColor: `${c.color}33` }}>
                {c.data.length}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 180,
                maxHeight: 300,
                overflowY: 'auto',
                padding: 10,
                borderRadius: 8,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                lineHeight: 1.8,
                color: colors.muted,
              }}
            >
              {c.data.length === 0 ? (
                <div style={{ textAlign: 'center', color: colors.dim, paddingTop: 60, fontSize: 12 }}>{t('toolCommon.noResult')}</div>
              ) : (
                c.data.map((m, i) => (
                  <div key={i} style={{ animation: 'fadeIn .3s ease' }}>
                    {m}
                  </div>
                ))
              )}
            </div>
            <button
              className="ms-btn ms-btn--ghost"
              onClick={() => onCopy(c.data.join('\n'))}
              style={{ marginTop: 8, width: '100%', padding: 6, fontSize: 11 }}
            >
              📋 Copy ({c.data.length})
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
